import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { useNavigate } from 'react-router-dom';

import NotificationList from '../notifications/NotificationList';
import { WaitingNotifications } from './WaitingRules';
import { InheritedRules } from './AutomationsTab';
import { getNotificationDefs } from '../../services/notifications';
import { isSubCollection, managesParentOf, subCollectionsOf } from '../../utils/collectionFamily';
import { PLUS } from '../icons';

const SEVERITY_LABELS = { info: 'Info', warning: 'Warning', critical: 'Critical' };

/**
 * Notifications tab (managers only; the tab itself is gated by
 * CollectionForm): what this collection notifies its people about, and what
 * it has decided about how. Grouped by category — at this tier a reader is
 * asking "which of my rules are these?", not "which collection is this?".
 *
 * Like Sharing, Automations and Add item, everything here applies IMMEDIATELY
 * through its own endpoints: it lives inside the settings page but outside
 * the Save-button lifecycle.
 *
 * A collection's answer is not the last word. A member can still turn a
 * channel off for themselves on their own account page — unless this tier
 * LOCKED it, which the row then says out loud as Required, with the reason.
 *
 * Authoring is the one thing that does NOT happen on this tab. A notification
 * carries seven panels and a live preview beside them; it gets its own route
 * (`…/notifications/new`), for the same reason a multi add form does.
 */
export default function NotificationsTab({ collectionId, collection }) {
	const navigate = useNavigate();
	// A sub-collection: the notifications defined on the collections above it
	// fire for its records too (docs/plans/sub-collections.md §4.7) — listed
	// read-only below its own, each with a door to where it is defined.
	const sub = isSubCollection(collection);
	const [inherited, setInherited] = useState([]);
	useEffect(() => {
		if (!sub || !collectionId) { setInherited([]); return undefined; }
		let live = true;
		getNotificationDefs({ tier: 'collection', collectionId })
			.then(({ inherited: rows }) => { if (live) setInherited(rows); })
			.catch(() => { if (live) setInherited([]); });
		return () => { live = false; };
	}, [sub, collectionId]);
	const subCount = subCollectionsOf(collection).length;
	// Bumped when a waiting notification is turned on, so the list reads again.
	const [listKey, setListKey] = useState(0);

	return (
		<div className="settings-column notifications-tab">
			<Alert
				type="info"
				showIcon
				message="Changes on this tab apply immediately"
				description="What you set here is the collection's default for everyone in it. Each person can still turn a channel off for themselves, unless it is locked."
			/>
			{subCount ? (
				<p className="notifications-tab__family-note meta-line" data-testid="notifications-family-note">
					{subCount === 1
						? 'Notifications here also fire for the records of its sub-collection.'
						: `Notifications here also fire for the records of its ${subCount} sub-collections.`}
				</p>
			) : null}
			<div className="notifications-tab__actions">
				<Button icon={<PLUS />} onClick={() => navigate(`/collections/${collectionId}/notifications/new`)}>
					New notification
				</Button>
			</div>
			{/* What an assistant or a key made, waiting for this person (KPR-196). */}
			<WaitingNotifications collectionId={collectionId} onChanged={() => setListKey(k => k + 1)} />
			<NotificationList
				key={listKey}
				tier="collection"
				scopeRef={{ collectionId, collectionName: collection?.name }}
				emptyText={inherited.length ? 'Nothing is defined here yet.' : 'This collection notifies nobody yet.'}
			/>
			<InheritedRules
				rules={inherited}
				managesParent={managesParentOf(collection)}
				tab="notifications"
				kindLabel={def => SEVERITY_LABELS[def?.severity] || null}
				describe={def => def?.description || def?.template?.title || null}
			/>
		</div>
	);
}
